import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  ArrowLeft, Shield, CheckCircle, AlertTriangle, XCircle, Clock,
  RefreshCw, Building2, Briefcase, ChevronDown, ChevronUp, Calendar, Eye, Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import ScoreBreakdownModal from './ScoreBreakdownModal';

const API = BACKEND_URL;

const LEVEL_LABELS = {
  level_1_basic: 'Level 1 - Basic',
  level_2_commitment: 'Level 2 - Commitment',
  level_3_excellence: 'Level 3 - Excellence',
  none: 'None',
};

const STATUS_CONFIG = {
  active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, iconColor: 'text-green-500', label: 'Active' },
  warning: { color: 'bg-amber-100 text-amber-800', icon: AlertTriangle, iconColor: 'text-amber-700', label: 'Expiring Soon' },
  critical: { color: 'bg-red-100 text-red-800', icon: AlertTriangle, iconColor: 'text-red-700', label: 'Expiring' },
  expired: { color: 'bg-red-200 text-red-900', icon: XCircle, iconColor: 'text-red-700', label: 'Expired' },
  revoked: { color: 'bg-gray-200 text-gray-800', icon: XCircle, iconColor: 'text-gray-700', label: 'Revoked' },
  no_expiry: { color: 'bg-blue-100 text-blue-800', icon: Shield, iconColor: 'text-blue-500', label: 'No Expiry Set' },
};

const CertificationRenewal = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [renewalDialog, setRenewalDialog] = useState(null);
  const [renewalNotes, setRenewalNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDialog, setRejectDialog] = useState(null);
  const [breakdownTarget, setBreakdownTarget] = useState(null); // { entity_type, entity_id, organization_name }

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/certification-renewal/overview`, { headers });
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch certification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestRenewal = async () => {
    if (!renewalDialog) return;
    try {
      await axios.post(`${API}/api/certification-renewal/request`, {
        entity_id: renewalDialog.entity_id,
        entity_type: renewalDialog.entity_type,
        notes: renewalNotes,
      }, { headers });
      toast.success('Renewal request submitted');
      setRenewalDialog(null);
      setRenewalNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit renewal request');
    }
  };

  const handleApproveRenewal = async (renewalId) => {
    try {
      await axios.patch(`${API}/api/certification-renewal/request/${renewalId}`, { status: 'approved' }, { headers });
      toast.success('Renewal approved — certification extended by 1 year');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve renewal');
    }
  };

  const handleRejectRenewal = async () => {
    if (!rejectDialog) return;
    try {
      await axios.patch(`${API}/api/certification-renewal/request/${rejectDialog}`, {
        status: 'rejected', reason: rejectReason
      }, { headers });
      toast.success('Renewal request rejected');
      setRejectDialog(null);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject renewal');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A';

  const filtered = (data?.certifications || []).filter(c => {
    if (filter !== 'all' && c.renewal_status !== filter) return false;
    if (typeFilter !== 'all' && c.entity_type !== typeFilter) return false;
    return true;
  });

  const stats = data?.stats || {};
  const isAdmin = user?.role === 'administrator' || user?.role === 'project_manager';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
          <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" /> Dashboard
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-blue-700" aria-hidden="true" /> Certification Renewal Tracking
          </h1>
          <p className="text-gray-700 mt-1">Monitor certification validity and manage renewal requests</p>
        </div>
      </div>

      {/* Stats */}
      <section aria-label="Certification statistics" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-blue-700', bg: '' },
          { label: 'Actors', value: stats.actors, color: 'text-indigo-700', bg: '' },
          { label: 'Investors', value: stats.investors, color: 'text-purple-700', bg: '' },
          { label: 'Active', value: stats.active, color: 'text-green-700', bg: 'bg-green-50/50' },
          { label: 'Warning', value: stats.warning, color: 'text-amber-700', bg: 'bg-amber-50/50' },
          { label: 'Critical', value: stats.critical, color: 'text-red-700', bg: 'bg-red-50/50' },
          { label: 'Expired', value: stats.expired, color: 'text-red-900', bg: 'bg-red-50/50' },
          { label: 'Renewals', value: stats.renewals_pending, color: 'text-blue-700', bg: 'bg-blue-50/50' },
        ].map((s, i) => (
          <Card key={i} className={s.bg}>
            <CardContent className="p-3 text-center">
              <span className={`block text-xl font-bold ${s.color}`}>{s.value || 0}</span>
              <p className="text-xs text-gray-700">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-gray-700 mr-2">Status:</span>
            {['all', 'active', 'warning', 'critical', 'expired'].map(f => (
              <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm"
                onClick={() => setFilter(f)} className={filter === f ? 'bg-gray-900' : ''}
                data-testid={`filter-${f}`}>
                {f === 'all' ? 'All' : STATUS_CONFIG[f]?.label || f}
              </Button>
            ))}
            <span className="text-sm text-gray-700 mx-2">Type:</span>
            {['all', 'actor', 'investor'].map(f => (
              <Button key={f} variant={typeFilter === f ? 'default' : 'outline'} size="sm"
                onClick={() => setTypeFilter(f)} className={typeFilter === f ? 'bg-gray-900' : ''}
                data-testid={`type-filter-${f}`}>
                {f === 'all' ? 'All' : f === 'actor' ? 'Actors' : 'Investors'}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Certification List */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="h-12 w-12 text-gray-400 mb-4" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-gray-800 mb-2">No Certifications Found</h2>
            <p className="text-gray-700">No certifications match the selected filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((cert) => {
            const sc = STATUS_CONFIG[cert.renewal_status] || STATUS_CONFIG.active;
            const Icon = sc.icon;
            const isExpanded = expandedId === cert.id;
            const daysLabel = cert.days_remaining !== null
              ? cert.days_remaining < 0 ? `Expired ${Math.abs(cert.days_remaining)} days ago`
                : `${cert.days_remaining} days remaining`
              : 'No expiry date';
            const progressVal = cert.days_remaining !== null
              ? Math.max(0, Math.min(100, Math.round((cert.days_remaining / 365) * 100)))
              : 100;

            return (
              <Card key={cert.id} data-testid={`cert-${cert.id}`}
                className={`transition-shadow hover:shadow-md ${
                  cert.renewal_status === 'critical' || cert.renewal_status === 'expired' ? 'border-l-4 border-l-red-400' :
                  cert.renewal_status === 'warning' ? 'border-l-4 border-l-amber-400' : ''
                }`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      cert.entity_type === 'actor' ? 'bg-blue-100' : 'bg-emerald-100'
                    }`}>
                      {cert.entity_type === 'actor'
                        ? <Building2 className="h-5 w-5 text-blue-700" />
                        : <Briefcase className="h-5 w-5 text-emerald-700" />}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{cert.organization_name}</span>
                        <Badge className={sc.color}>{sc.label}</Badge>
                        <Badge variant="outline" className="text-xs capitalize">{cert.entity_type}</Badge>
                        <Badge variant="outline" className="text-xs">{LEVEL_LABELS[cert.certification_level] || cert.certification_level}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-700">
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" aria-hidden="true" /> Certified: {formatDate(cert.certification_date)}</span>
                        <span>Expires: {formatDate(cert.valid_until)}</span>
                        <span className={`font-medium ${cert.days_remaining !== null && cert.days_remaining <= 30 ? 'text-red-700' : cert.days_remaining !== null && cert.days_remaining <= 90 ? 'text-amber-700' : 'text-gray-700'}`}>
                          {daysLabel}
                        </span>
                      </div>
                    </div>

                    {/* Progress + Actions */}
                    <div className="hidden md:flex items-center gap-3">
                      <div className="w-24 text-center">
                        <Progress value={progressVal} className={`h-2 mb-1 ${cert.renewal_status === 'expired' ? '[&>div]:bg-red-600' : cert.renewal_status === 'critical' ? '[&>div]:bg-red-400' : cert.renewal_status === 'warning' ? '[&>div]:bg-amber-400' : ''}`} />
                        <span className="text-xs text-gray-700">{cert.days_remaining !== null ? `${Math.max(0, cert.days_remaining)}d` : '—'}</span>
                      </div>
                      {cert.renewal_requested && cert.renewal_request?.status === 'pending' ? (
                        isAdmin ? (
                          <div className="flex gap-1">
                            <Button size="sm" className="bg-green-700 hover:bg-green-800 h-8 text-xs"
                              onClick={(e) => { e.stopPropagation(); handleApproveRenewal(cert.renewal_request.id); }}
                              data-testid={`approve-${cert.id}`}>
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs text-red-700 border-red-300"
                              onClick={(e) => { e.stopPropagation(); setRejectDialog(cert.renewal_request.id); }}
                              data-testid={`reject-${cert.id}`}>
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700">Renewal Pending</Badge>
                        )
                      ) : cert.renewal_requested && cert.renewal_request?.status === 'approved' ? (
                        <Badge className="bg-green-100 text-green-700">Renewed</Badge>
                      ) : (
                        (cert.renewal_status === 'warning' || cert.renewal_status === 'critical' || cert.renewal_status === 'expired') && (
                          <Button size="sm" variant="outline" className="h-8 text-xs"
                            onClick={(e) => { e.stopPropagation(); setRenewalDialog(cert); }}
                            data-testid={`renew-${cert.id}`}>
                            <RefreshCw className="h-3 w-3 mr-1" aria-hidden="true" /> Request Renewal
                          </Button>
                        )
                      )}
                    </div>

                    <Button variant="ghost" size="sm" onClick={() => setExpandedId(isExpanded ? null : cert.id)} aria-label={isExpanded ? `Collapse ${cert.organization_name} details` : `Expand ${cert.organization_name} details`} data-testid={`toggle-cert-${cert.id}`}>
                      {isExpanded ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                    </Button>
                  </div>

                  {/* Expanded Scores */}
                  {isExpanded && (cert.effective_scores || cert.scores) && Object.keys(cert.effective_scores || cert.scores).length > 0 && (() => {
                    const displayScores = cert.effective_scores || cert.scores;
                    const overrides = cert.score_overrides || {};
                    const hasOverrides = Object.keys(overrides).length > 0;
                    return (
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-gray-700">Assessment Scores</p>
                            {hasOverrides && (
                              <Badge className="bg-amber-100 text-amber-800 text-xs">
                                <Edit3 className="h-3 w-3 mr-1" aria-hidden="true" /> Overridden
                              </Badge>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => setBreakdownTarget({
                              entity_type: cert.entity_type,
                              entity_id: cert.entity_id,
                              organization_name: cert.organization_name,
                            })}
                            data-testid={`view-breakdown-${cert.id}`}
                          >
                            <Eye className="h-3 w-3 mr-1" aria-hidden="true" /> View Breakdown {isAdmin ? '& Override' : ''}
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {Object.entries(displayScores).filter(([k]) => k !== 'overall').map(([key, value]) => {
                            const isOverridden = overrides[key] !== undefined && overrides[key] !== null;
                            return (
                              <div key={key} className={`rounded-lg p-2 ${isOverridden ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                                <div className="flex items-center justify-between">
                                  <p className="text-xs text-gray-700 capitalize">{key.replace(/_/g, ' ')}</p>
                                  {isOverridden && <Edit3 className="h-3 w-3 text-amber-700" />}
                                </div>
                                <p className="text-sm font-medium text-gray-800">{typeof value === 'number' ? `${Math.round(value)}%` : String(value)}</p>
                              </div>
                            );
                          })}
                        </div>
                        {displayScores.overall !== undefined && (
                          <div className="mt-2 bg-blue-50 rounded-lg p-3 flex items-center justify-between">
                            <span className="text-sm font-medium text-blue-800">Overall Score</span>
                            <span className="text-lg font-bold text-blue-700">{Math.round(displayScores.overall)}%</span>
                          </div>
                        )}
                        {/* Mobile actions */}
                        <div className="md:hidden mt-3 flex gap-2">
                          {cert.renewal_requested && cert.renewal_request?.status === 'pending' && isAdmin ? (
                            <>
                              <Button size="sm" className="bg-green-700 hover:bg-green-800"
                                onClick={() => handleApproveRenewal(cert.renewal_request.id)}>Approve</Button>
                              <Button size="sm" variant="outline" className="text-red-700"
                                onClick={() => setRejectDialog(cert.renewal_request.id)}>Reject</Button>
                            </>
                          ) : (cert.renewal_status === 'warning' || cert.renewal_status === 'critical' || cert.renewal_status === 'expired') && !cert.renewal_requested ? (
                            <Button size="sm" variant="outline" onClick={() => setRenewalDialog(cert)}>
                              <RefreshCw className="h-3 w-3 mr-1" aria-hidden="true" /> Request Renewal
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Renewal Request Dialog */}
      <Dialog open={!!renewalDialog} onOpenChange={() => setRenewalDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Certification Renewal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-700">
              Requesting renewal for <strong>{renewalDialog?.organization_name}</strong> ({renewalDialog?.entity_type})
            </p>
            <div>
              <Textarea placeholder="Notes (optional) — reason for renewal, updated documentation, etc."
                value={renewalNotes} onChange={(e) => setRenewalNotes(e.target.value)} rows={3} />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setRenewalDialog(null)}>Cancel</Button>
              <Button onClick={handleRequestRenewal} className="bg-blue-700 hover:bg-blue-800" data-testid="submit-renewal-btn">
                <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" /> Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject Renewal Request</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <Textarea placeholder="Reason for rejection..." value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)} rows={3} />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setRejectDialog(null)}>Cancel</Button>
              <Button onClick={handleRejectRenewal} className="bg-red-700 hover:bg-red-800">Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Score Breakdown Modal */}
      {breakdownTarget && (
        <ScoreBreakdownModal
          open={!!breakdownTarget}
          onClose={() => setBreakdownTarget(null)}
          entityType={breakdownTarget.entity_type}
          entityId={breakdownTarget.entity_id}
          entityName={breakdownTarget.organization_name}
          isAdmin={isAdmin}
          onChanged={fetchData}
        />
      )}
    </div>
  );
};

export default CertificationRenewal;
